import React, { useState, useEffect, useCallback } from "react";
import { X, ChevronLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import { ChatView, Message, PREDEFINED_OPTIONS } from "./chat.types";
import { ChatWelcome } from "./ChatWelcome";
import { ChatConversation } from "./ChatConversation";
import { useServerFn } from "@tanstack/react-start";
import { getAIChatResponse } from "@/lib/admin/ai-chat.functions";

interface ChatPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ChatPanel({ isOpen, onClose }: ChatPanelProps) {
  const [view, setView] = useState<ChatView>("welcome");
  const [messages, setMessages] = useState<Message[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const fetchAIResponse = useServerFn(getAIChatResponse);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  const handleSelectOption = (optionId: string) => {
    const option = PREDEFINED_OPTIONS.find((o) => o.id === optionId);
    if (!option) return;

    const customerMsg: Message = {
      id: Date.now().toString(),
      role: "customer",
      text: option.label,
      timestamp: new Date(),
    };

    const assistantMsg: Message = {
      id: (Date.now() + 1).toString(),
      role: "assistant",
      text: option.response,
      timestamp: new Date(),
    };

    setMessages([customerMsg, assistantMsg]);
    setView("conversation");
  };

  const handleSendMessage = async (text: string) => {
    const customerMsg: Message = {
      id: Date.now().toString(),
      role: "customer",
      text,
      timestamp: new Date(),
    };
    
    setMessages(prev => [...prev, customerMsg]);
    setIsProcessing(true);

    try {
      const { response } = await fetchAIResponse({ data: { message: text, isDemo: false } });
      const assistantMsg: Message = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        text: response,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, assistantMsg]);
    } catch (err) {
      console.error("[customer-chat] AI error:", err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBack = () => {
    setView("welcome");
  };

  return (
    <div
      className={cn(
        "fixed bottom-4 right-4 z-[90] flex w-[calc(100vw-32px)] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl transition-all duration-300 ease-in-out sm:bottom-8 sm:right-8 sm:w-[400px]",
        isOpen 
          ? "translate-y-0 opacity-100 pointer-events-auto max-h-[600px] h-[calc(100vh-64px)] sm:h-[600px]" 
          : "translate-y-10 opacity-0 pointer-events-none max-h-0"
      )}
      role="dialog"
      aria-modal="true"
      aria-label="Healthy Kitchen Chat Support"
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b px-4 py-3 bg-primary text-primary-foreground">
        <div className="flex items-center gap-2">
          {view === "conversation" && (
            <button
              onClick={handleBack}
              className="mr-1 rounded-full p-1 hover:bg-white/10"
              aria-label="Back to options"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          )}
          <div>
            <h3 className="text-sm font-bold leading-none">
              {view === "welcome" ? "Healthy Kitchen Support" : "Healthy Kitchen Expert"}
            </h3>
            <div className="mt-1 flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-green-400 animate-pulse"></span>
              <span className="text-[10px] uppercase tracking-wider opacity-90">Online</span>
            </div>
          </div>
        </div>
        <button
          onClick={onClose}
          className="rounded-full p-1 hover:bg-white/10"
          aria-label="Close chat"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {view === "welcome" ? (
          <ChatWelcome onSelectOption={handleSelectOption} />
        ) : (
          <ChatConversation 
            messages={messages} 
            onSendMessage={handleSendMessage}
            onBack={handleBack}
            isProcessing={isProcessing}
          />
        )}
      </div>
      
      {/* Footer Branding */}
      <div className="border-t py-2 text-center">
        <span className="text-[10px] text-muted-foreground uppercase tracking-widest">
          Powered by Healthy Kitchen AI
        </span>
      </div>
    </div>
  );
}
